import {
    CONDITION_INSPECTION,
    ID_CONDITION_INSPECTION,
    PRE_PSC_INSPECTION
} from "../constants/InspectionTypes";

export type InspectionTabKey =
    | "summary"
    | "particulars"
    | "notable_items"
    | "grade_comparison"
    | "grading"
    | "defects"
    | "decarbonisation"
    | "documents"
    | "supplements"
    | "images"
    | "adjusted_grading";

export interface InspectionTabLayout {
    /** Every tab the inspection type can show, in display order. */
    tabs: InspectionTabKey[];
    /** Render the Grading tab in its ID report form. */
    idReport?: boolean;
    /** i18n key naming the defects tab; falls back to `tabs.defects`. */
    defectsTitleKey?: string;
    /** The inspection type carries no Idwal grade, e.g. in the report header. */
    ungraded?: boolean;
    /**
     * The Defects tab shows only the regime (MoU) codes, not the Idwal one,
     * and only the defects that carry one, as the inspection's PDF does.
     */
    regimeCodesOnly?: boolean;
}

export interface ResolvedInspectionTab {
    key: InspectionTabKey;
    /** i18n key for the tab title, in the `locale` namespace. */
    titleKey: string;
}

export interface ResolvedInspectionLayout {
    tabs: ResolvedInspectionTab[];
    idReport: boolean;
    regimeCodesOnly: boolean;
}

export interface ResolveInspectionTabsOptions {
    isFullReport: boolean;
    hasAdjustedGrading: boolean;
}

const DEFAULT_LAYOUT: InspectionTabLayout = {
    tabs: [
        "summary",
        "particulars",
        "notable_items",
        "grading",
        "decarbonisation",
        "documents",
        "supplements",
        "images",
        "grade_comparison",
        "adjusted_grading"
    ]
};

const INSPECTION_TAB_LAYOUTS: Record<string, InspectionTabLayout> = {
    [ID_CONDITION_INSPECTION]: {
        tabs: [
            "summary",
            "particulars",
            "grade_comparison",
            "grading",
            "defects",
            "decarbonisation",
            "documents",
            "supplements",
            "images",
            "adjusted_grading"
        ],
        idReport: true
    },
    [CONDITION_INSPECTION]: {
        tabs: [
            "summary",
            "particulars",
            "notable_items",
            "grading",
            "decarbonisation",
            "documents",
            "supplements",
            "images",
            "adjusted_grading"
        ]
    },
    // Pre-PSC is ungraded, so it carries no grading or adjusted grading tabs.
    [PRE_PSC_INSPECTION]: {
        ungraded: true,
        regimeCodesOnly: true,
        tabs: [
            "summary",
            "particulars",
            "defects",
            "documents",
            "supplements",
            "images"
        ],
        defectsTitleKey: "tabs.deficiencies"
    }
};

const getLayout = (inspectionType: string | undefined) =>
    (inspectionType && INSPECTION_TAB_LAYOUTS[inspectionType]) ||
    DEFAULT_LAYOUT;

export const isGradedInspectionType = (inspectionType: string | undefined) =>
    !getLayout(inspectionType).ungraded;

export const resolveInspectionTabs = (
    inspectionType: string | undefined,
    options: ResolveInspectionTabsOptions
): ResolvedInspectionLayout => {
    const layout = getLayout(inspectionType);
    const isAvailable: Partial<Record<InspectionTabKey, boolean>> = {
        grade_comparison: options.isFullReport,
        adjusted_grading: options.hasAdjustedGrading
    };

    return {
        tabs: layout.tabs
            .filter((key) => isAvailable[key] ?? true)
            .map((key) => ({
                key,
                titleKey:
                    key === "defects" && layout.defectsTitleKey
                        ? layout.defectsTitleKey
                        : `tabs.${key}`
            })),
        idReport: layout.idReport ?? false,
        regimeCodesOnly: layout.regimeCodesOnly ?? false
    };
};
